import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import { Users, HardHat, FolderKanban, Send, CheckCircle2, AlertTriangle, ArrowRight, RefreshCw } from "lucide-react";

const DS = {
  indigo: "#4F46E5", indigoMid: "#6366F1", indigoLight: "#EEF2FF", indigoDark: "#3730A3",
  teal: "#0D9488", tealLight: "#F0FDFA",
  orange: "#EA580C", orangeLight: "#FFF7ED",
  border: "#E2E8F0", borderSolid: "#E2E8F0",
  dark: "#0F172A", gray: "#64748B", faint: "#94A3B8", grayLight: "#F1F5F9",
  surface: "#FFFFFF", surfaceAlt: "#F8FAFC",
  emerald: "#059669", rose: "#E11D48",
};

export default function ReassignProject() {
  const isSuperAdmin = (localStorage.getItem("role") || "").toLowerCase() === "admin" || Number(localStorage.getItem("is_admin")) === 1;
  const loggedInUserId = localStorage.getItem("id");

  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState("");

  const [engineers, setEngineers] = useState([]);
  const [selectedEngineerId, setSelectedEngineerId] = useState("");

  const [projects, setProjects] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  const [reassignManagers, setReassignManagers] = useState([]);
  const [newManagerId, setNewManagerId] = useState("");
  const [newEngineerOptions, setNewEngineerOptions] = useState([]);
  const [newEngineerId, setNewEngineerId] = useState("");

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Load managers (super admin only) and their own reassign-to manager list
  useEffect(() => {
    if (isSuperAdmin) {
      API.get("/admin/reassign/managers")
        .then(res => {
          if (res.data?.success) {
            setManagers(res.data.managers || []);
            setReassignManagers(res.data.managers || []);
          }
        })
        .catch(err => console.error("Fetch managers error:", err));
    }
  }, [isSuperAdmin]);

  // Load engineers: scoped to selected manager (super admin) or own team (manager)
  useEffect(() => {
    if (isSuperAdmin) {
      if (!selectedManagerId) { setEngineers([]); setSelectedEngineerId(""); return; }
      API.get(`/admin/reassign/engineers-by-manager/${selectedManagerId}`)
        .then(res => { if (res.data?.success) setEngineers(res.data.engineers || []); })
        .catch(err => console.error("Fetch engineers error:", err));
    } else {
      API.get(`/admin/reassign/engineers-by-manager/${loggedInUserId}`)
        .then(res => { if (res.data?.success) setEngineers(res.data.engineers || []); })
        .catch(err => console.error("Fetch engineers error:", err));
    }
  }, [isSuperAdmin, selectedManagerId, loggedInUserId]);

  // Load "reassign to" engineer options: scoped to target manager
  useEffect(() => {
    const targetManagerId = isSuperAdmin
      ? (newManagerId || selectedManagerId)
      : loggedInUserId;

    if (!targetManagerId) {
      setNewEngineerOptions([]);
      setNewEngineerId("");
      return;
    }

    API.get(`/admin/reassign/engineers-by-manager/${targetManagerId}`)
      .then(res => {
        if (res.data?.success) {
          const engs = res.data.engineers || [];
          setNewEngineerOptions(engs);
          setNewEngineerId(prev => (engs.some(e => String(e.id) === String(prev)) ? prev : ""));
        } else {
          setNewEngineerOptions([]);
          setNewEngineerId("");
        }
      })
      .catch(err => {
        console.error("Fetch target manager engineers error:", err);
        setNewEngineerOptions([]);
        setNewEngineerId("");
      });
  }, [isSuperAdmin, newManagerId, selectedManagerId, loggedInUserId]);

  // Load projects for selected engineer
  useEffect(() => {
    if (!selectedEngineerId) { setProjects([]); setSelectedProjectIds([]); return; }
    API.get(`/admin/reassign/projects-by-engineer/${selectedEngineerId}`)
      .then(res => { if (res.data?.success) setProjects(res.data.projects || []); })
      .catch(err => console.error("Fetch projects error:", err));
  }, [selectedEngineerId]);

  const toggleProject = (id) => {
    setSelectedProjectIds(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProjectIds.length === projects.length) setSelectedProjectIds([]);
    else setSelectedProjectIds(projects.map(p => p.id));
  };

  const handleReassign = async () => {
    if (selectedProjectIds.length === 0) {
      setToast({ message: "Please select at least one project.", type: "error" }); return;
    }
    if (!newEngineerId) {
      setToast({ message: "Please select an engineer to reassign to.", type: "error" }); return;
    }
    setSaving(true);
    try {
      const payload = {
        projectAssignmentIds: selectedProjectIds,
        newEngineerId,
        ...(isSuperAdmin && newManagerId ? { newManagerId } : {}),
      };
      const res = await API.post("/admin/reassign", payload);
      if (res.data.success) {
        setToast({ message: "Project(s) reassigned successfully!", type: "success" });
        setSelectedProjectIds([]); setNewManagerId(""); setNewEngineerId("");
        if (selectedEngineerId) {
          const refreshed = await API.get(`/admin/reassign/projects-by-engineer/${selectedEngineerId}`);
          if (refreshed.data?.success) setProjects(refreshed.data.projects || []);
        }
      } else {
        setToast({ message: res.data.message || "Reassignment failed.", type: "error" });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || "Error reassigning project(s).", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const selectedEngineerName = engineers.find(e => String(e.id) === String(selectedEngineerId))?.name;
  const newEngineerName = newEngineerOptions.find(e => String(e.id) === String(newEngineerId))?.name;

  return (
    <AdminLayout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        .rp-wrap * { font-family: 'Montserrat', sans-serif; }
        .rp-select { transition: border-color 0.15s, box-shadow 0.15s; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%2394A3B8' d='M1 1l5 5 5-5'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 36px !important; }
        .rp-select:focus { border-color: ${DS.indigo} !important; box-shadow: 0 0 0 3px rgba(79,70,229,0.12); outline: none; }
        .rp-select:disabled { cursor: not-allowed !important; }
        .rp-proj-row { transition: all 0.15s; }
        .rp-proj-row:hover { border-color: ${DS.indigo}99 !important; transform: translateY(-1px); }
        .rp-reassign-btn { transition: all 0.18s; }
        .rp-reassign-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(234,88,12,0.42) !important; }
        .rp-toggle-all { transition: all 0.15s; }
        .rp-toggle-all:hover { background: ${DS.indigo} !important; color: #fff !important; }
        @keyframes rp-toast-in { from { opacity:0; transform:translateY(12px) scale(0.97); } to { opacity:1; transform:none; } }
        @keyframes rp-fade-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
        .rp-step-2, .rp-step-3 { animation: rp-fade-in 0.25s ease; }
      `}</style>

      {toast && (
        <div style={{
          position: "fixed", top: 24, right: 24, zIndex: 9999,
          background: toast.type === "error" ? DS.rose : DS.emerald, color: "#fff",
          padding: "14px 18px", borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          display: "flex", alignItems: "center", gap: 12, fontWeight: 700, fontSize: 14,
          animation: "rp-toast-in 0.22s ease", minWidth: 260,
        }}>
          {toast.type === "error" ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
          {toast.message}
          <button onClick={() => setToast(null)} style={{ marginLeft: "auto", background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", width: 20, height: 20, borderRadius: "50%", cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
        </div>
      )}

      <div className="rp-wrap" style={{ padding: "28px 32px 56px", maxWidth: 1040, margin: "0 auto" }}>

        {/* ── PAGE HEADER ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 4 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: "linear-gradient(135deg,#4F46E5,#6366F1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 6px 18px rgba(79,70,229,0.32)", flexShrink: 0,
          }}>
            <Send size={21} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontWeight: 800, fontSize: 25, color: DS.dark, margin: 0, letterSpacing: "-0.4px" }}>
              Reassign Projects
            </h1>
            <p style={{ color: DS.gray, fontSize: 13, margin: "3px 0 0" }}>
              {isSuperAdmin
                ? "Select a manager, then an engineer to view and reassign their projects."
                : "Select an engineer from your team to view and reassign their projects."}
            </p>
          </div>
        </div>

        {/* ── STEP 1: SELECT SOURCE ── */}
        <div style={{
          background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`,
          overflow: "hidden", marginTop: 26, boxShadow: "0 2px 12px rgba(79,70,229,0.06)",
        }}>
          <div style={{ height: 4, background: "linear-gradient(90deg,#4F46E5,#6366F1)" }} />
          <div style={{ padding: "24px 28px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20 }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: DS.indigoLight, border: `1.5px solid #C7D2FE`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: DS.indigo, flexShrink: 0 }}>1</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: DS.dark }}>Choose whose projects to view</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isSuperAdmin ? "1fr 1fr" : "1fr", gap: 18 }}>
              {isSuperAdmin && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                    <Users size={13} /> Manager
                  </label>
                  <select className="rp-select" value={selectedManagerId} onChange={e => {
                    setSelectedManagerId(e.target.value);
                    setSelectedEngineerId("");
                    setSelectedProjectIds([]);
                    setNewManagerId("");
                    setNewEngineerId("");
                  }}
                    style={{ width: "100%", padding: "13px 14px", borderRadius: 12, border: `1.5px solid ${DS.border}`, fontSize: 14, color: DS.dark, background: DS.surfaceAlt, cursor: "pointer", fontWeight: 500, boxSizing: "border-box" }}>
                    <option value="">— Select Manager —</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                  <HardHat size={13} /> Engineer
                </label>
                <select className="rp-select" value={selectedEngineerId} onChange={e => setSelectedEngineerId(e.target.value)}
                  disabled={isSuperAdmin && !selectedManagerId}
                  style={{ width: "100%", padding: "13px 14px", borderRadius: 12, border: `1.5px solid ${DS.border}`, fontSize: 14, color: DS.dark, background: (isSuperAdmin && !selectedManagerId) ? DS.grayLight : DS.surfaceAlt, cursor: (isSuperAdmin && !selectedManagerId) ? "not-allowed" : "pointer", fontWeight: 500, boxSizing: "border-box" }}>
                  <option value="">— Select Engineer —</option>
                  {engineers.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ── STEP 2: PROJECT LIST ── */}
        {selectedEngineerId && (
          <div className="rp-step-2" style={{
            background: DS.surface, borderRadius: 18, border: `1.5px solid ${DS.border}`,
            overflow: "hidden", marginTop: 20, boxShadow: "0 2px 12px rgba(13,148,136,0.08)",
          }}>
            <div style={{ height: 4, background: "linear-gradient(90deg,#0D9488,#14B8A6)" }} />
            <div style={{ padding: "24px 28px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 9, background: DS.tealLight, border: `1.5px solid #99F6E4`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: DS.teal, flexShrink: 0 }}>2</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: DS.dark, display: "flex", alignItems: "center", gap: 8 }}>
                      <FolderKanban size={16} color={DS.teal} />
                      Projects assigned to {selectedEngineerName || "engineer"}
                    </div>
                    <div style={{ fontSize: 12, color: DS.faint, marginTop: 2 }}>
                      {projects.length} project{projects.length !== 1 ? "s" : ""} found
                      {selectedProjectIds.length > 0 && <span style={{ color: DS.indigo, fontWeight: 700 }}> · {selectedProjectIds.length} selected</span>}
                    </div>
                  </div>
                </div>
                {projects.length > 0 && (
                  <button onClick={toggleSelectAll} className="rp-toggle-all" style={{ fontSize: 12, fontWeight: 700, color: DS.indigo, background: DS.indigoLight, border: `1.5px solid #C7D2FE`, borderRadius: 10, padding: "8px 16px", cursor: "pointer", whiteSpace: "nowrap" }}>
                    {selectedProjectIds.length === projects.length ? "Deselect All" : "Select All"}
                  </button>
                )}
              </div>

              {projects.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: DS.faint }}>
                  <div style={{ width: 56, height: 56, borderRadius: 16, background: DS.grayLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                    <FolderKanban size={24} color={DS.faint} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: DS.gray }}>No projects assigned to this engineer.</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  {projects.map(p => {
                    const checked = selectedProjectIds.includes(p.id);
                    return (
                      <label key={p.id} className="rp-proj-row" style={{
                        display: "flex", alignItems: "center", gap: 13, padding: "14px 16px",
                        border: `1.5px solid ${checked ? DS.indigo : DS.border}`, borderRadius: 12, cursor: "pointer",
                        background: checked ? DS.indigoLight : DS.surfaceAlt,
                        boxShadow: checked ? "0 2px 8px rgba(79,70,229,0.12)" : "none",
                      }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                          border: `2px solid ${checked ? DS.indigo : DS.border}`,
                          background: checked ? DS.indigo : "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.15s",
                        }}>
                          {checked && <CheckCircle2 size={13} color="#fff" strokeWidth={3} />}
                        </div>
                        <input type="checkbox" checked={checked} onChange={() => toggleProject(p.id)} style={{ display: "none" }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 700, fontSize: 13.5, color: DS.dark }}>{p.project}</span>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: DS.indigo, background: "#fff", border: `1px solid #C7D2FE`, padding: "4px 11px", borderRadius: 20, flexShrink: 0, letterSpacing: "0.02em" }}>{p.project_id}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP 3: REASSIGN TO ── */}
        {selectedProjectIds.length > 0 && (
          <div className="rp-step-3" style={{
            background: DS.surface, borderRadius: 18, border: `1.5px solid #FDBA74`,
            overflow: "hidden", marginTop: 20, boxShadow: "0 6px 24px rgba(234,88,12,0.14)",
          }}>
            <div style={{ height: 4, background: "linear-gradient(90deg,#EA580C,#F97316)" }} />
            <div style={{ padding: "24px 28px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20 }}>
                <div style={{ width: 30, height: 30, borderRadius: 9, background: DS.orangeLight, border: `1.5px solid #FED7AA`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: DS.orange, flexShrink: 0 }}>3</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: DS.dark, display: "flex", alignItems: "center", gap: 8 }}>
                  <Send size={16} color={DS.orange} /> Reassign To
                </div>
              </div>

              {/* Transfer preview strip */}
              {(selectedEngineerName || newEngineerName) && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 12, marginBottom: 20,
                  background: DS.surfaceAlt, border: `1.5px dashed ${DS.border}`, borderRadius: 12, padding: "12px 16px",
                  flexWrap: "wrap",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: DS.grayLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <HardHat size={13} color={DS.gray} />
                    </div>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: DS.gray }}>{selectedEngineerName || "Current engineer"}</span>
                  </div>
                  <ArrowRight size={15} color={DS.faint} />
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: newEngineerName ? DS.orangeLight : DS.grayLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <HardHat size={13} color={newEngineerName ? DS.orange : DS.faint} />
                    </div>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: newEngineerName ? DS.orange : DS.faint }}>{newEngineerName || "Select new engineer"}</span>
                  </div>
                  <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: DS.gray, background: "#fff", border: `1px solid ${DS.border}`, padding: "3px 10px", borderRadius: 20 }}>
                    {selectedProjectIds.length} project{selectedProjectIds.length !== 1 ? "s" : ""}
                  </span>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: isSuperAdmin ? "1fr 1fr" : "1fr", gap: 18, marginBottom: 22 }}>
                {isSuperAdmin && (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                      <Users size={13} /> New Manager
                    </label>
                    <select className="rp-select" value={newManagerId} onChange={e => {
                      setNewManagerId(e.target.value);
                      setNewEngineerId("");
                    }}
                      style={{ width: "100%", padding: "13px 14px", borderRadius: 12, border: `1.5px solid ${DS.border}`, fontSize: 14, color: DS.dark, background: DS.surfaceAlt, cursor: "pointer", fontWeight: 500, boxSizing: "border-box" }}>
                      <option value="">— Keep current manager —</option>
                      {reassignManagers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, display: "flex", alignItems: "center", gap: 6, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                    <HardHat size={13} /> New Engineer <span style={{ color: DS.rose }}>*</span>
                  </label>
                  <select className="rp-select" value={newEngineerId} onChange={e => setNewEngineerId(e.target.value)}
                    style={{ width: "100%", padding: "13px 14px", borderRadius: 12, border: `1.5px solid ${DS.border}`, fontSize: 14, color: DS.dark, background: DS.surfaceAlt, cursor: "pointer", fontWeight: 500, boxSizing: "border-box" }}>
                    <option value="">— Select Engineer —</option>
                    {newEngineerOptions.length === 0 ? (
                      <option value="" disabled>No engineers assigned to this manager</option>
                    ) : (
                      newEngineerOptions.map(e => <option key={e.id} value={e.id}>{e.name}</option>)
                    )}
                  </select>
                </div>
              </div>

              <button onClick={handleReassign} disabled={saving} className="rp-reassign-btn"
                style={{
                  background: saving ? DS.grayLight : "linear-gradient(135deg,#EA580C,#F97316)",
                  color: saving ? DS.gray : "#fff", border: "none", padding: "14px 30px", borderRadius: 12,
                  fontWeight: 700, fontSize: 14.5, cursor: saving ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: 9,
                  boxShadow: saving ? "none" : "0 4px 18px rgba(234,88,12,0.32)",
                }}>
                {saving ? <RefreshCw size={16} style={{ animation: "rp-spin 0.8s linear infinite" }} /> : <Send size={16} />}
                {saving ? "Reassigning…" : `Reassign ${selectedProjectIds.length} Project${selectedProjectIds.length !== 1 ? "s" : ""}`}
              </button>
              <style>{`@keyframes rp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}